/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Row,
  Spinner,
  Button,
  Badge,
} from "reactstrap";
import LocationSearch from "../../../../helpers/Common/LocationSearch";
import SearchableDropdown from "../../../../helpers/Common/SearchableDropdown";
import { getDropdownOptions } from "../../../../helpers/common";
import { getCategories, getJobIndustries } from "../../../../api/categories";
import { getFrontendSettings } from "../../../../api/settings";
import { getSettingByKey, toArray } from "../../../../helpers/common";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const ManageJobsForm = ({ onSubmit, loading, jobDetails, formType }) => {
  const [categories, setCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const employer = useSelector((state) => state.employer);
  const companies = employer?.companies?.data || [];
  const [errors, setErrors] = useState({});
  const [modal, setModal] = useState(false);
  const [showOtherLocation, setShowOtherLocation] = useState(false);
  const [otherLocation, setOtherLocation] = useState("");

  // Preview Modal State
  const [previewModal, setPreviewModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Dynamic Settings Options Hooks
  const [experienceOptions, setExperienceOptions] = useState([]);
  const [salaryOptions, setSalaryOptions] = useState([]);
  const settings = useSelector((state) => state.settings.data);

  const jobLocationsReducer = useSelector((state) => state.jobLocations);
  const jobLocationsList = jobLocationsReducer?.data || [];

  const ganderOptionsList = toArray(
    getSettingByKey(settings, "Gender")?.value ||
      getSettingByKey(settings, "Gander")?.value,
    "\n"
  );

  const INITIAL_STATE = {
    jobTitle: jobDetails?.jobTitle || "",
    experience: jobDetails?.experience || "",
    department: jobDetails?.department || "",
    location: jobDetails?.location || "",
    description: jobDetails?.description || "",
    url: jobDetails?.url || "",
    isActive: jobDetails?.isActive ?? 0,
    displayOrder: jobDetails?.displayOrder ?? 0,
    qualificationsRequired: jobDetails?.qualificationsRequired || "",
    // 🎯 starts empty for new jobs so the user must enter a real number
    totalNumberOfVacancies: jobDetails?.totalNumberOfVacancies ?? "",
    minimumExperienceRequired: jobDetails?.minimumExperienceRequired || "",
    maximumExperienceRequired: jobDetails?.maximumExperienceRequired || "",
    minimumSalaryPerMonth: jobDetails?.minimumSalaryPerMonth || "",
    maximumSalaryPerMonth: jobDetails?.maximumSalaryPerMonth || "",
    job_type: jobDetails?.job_type || "",
    gander: jobDetails?.gander || "",
    jobPriority: jobDetails?.jobPriority || "",
    jobPosition: jobDetails?.jobPosition || "",
    jobResponsibilities: jobDetails?.jobResponsibilities || "",
    skillsAndExperience: jobDetails?.skillsAndExperience || "",
    searchKeywords: jobDetails?.searchKeywords || "",
    facilitiesProvided: jobDetails?.facilitiesProvided || "",
    employerId: jobDetails?.employerId ?? 107,
    jobCompanyId: jobDetails?.jobCompanyId ?? 82,
    jobLocationId: jobDetails?.jobLocationId ?? 3,
    jobTypeCategoryId: jobDetails?.jobTypeCategoryId ?? 3,
    jobStatus: jobDetails?.jobStatus || "Open",
  };

  const [formData, setFormData] = useState(INITIAL_STATE);

  // Fetch Categories on Mount
  useEffect(() => {
    setApiLoading(true);
    getCategories()
      .then((res) => {
        const arrayData = res?.data?.data || [];
        setCategories(arrayData);
      })
      .catch((err) => {
        console.error("Categories fetch error:", err);
      })
      .finally(() => {
        setApiLoading(false);
      });
  }, []);

  useEffect(() => {
    if (jobDetails) {
      const matchedLocation = jobLocationsList.find(
        (loc) => String(loc.id) === String(jobDetails.jobLocationId)
      );

      const resolvedLocationName =
        matchedLocation?.state ||
        (typeof jobDetails.location === "string" ? jobDetails.location : "");

      setFormData({
        jobTitle: jobDetails.jobTitle || "",
        experience: jobDetails.experience || "",
        department: jobDetails.department || "",
        location: resolvedLocationName,
        description: jobDetails.description || "",
        url: jobDetails.url || "",
        isActive: jobDetails.isActive ?? 0,
        displayOrder: jobDetails.displayOrder ?? 0,
        qualificationsRequired: jobDetails.qualificationsRequired || "",
        totalNumberOfVacancies: jobDetails.totalNumberOfVacancies ?? "",
        minimumExperienceRequired: jobDetails.minimumExperienceRequired || "",
        maximumExperienceRequired: jobDetails.maximumExperienceRequired || "",
        minimumSalaryPerMonth: jobDetails.minimumSalaryPerMonth || "",
        maximumSalaryPerMonth: jobDetails.maximumSalaryPerMonth || "",
        job_type: jobDetails.job_type || "",
        gander: jobDetails.gander || "",
        jobPriority: jobDetails.jobPriority || "",
        jobPosition: jobDetails.jobPosition || "",
        jobResponsibilities: jobDetails.jobResponsibilities || "",
        skillsAndExperience: jobDetails.skillsAndExperience || "",
        searchKeywords: jobDetails.searchKeywords || "",
        facilitiesProvided: jobDetails.facilitiesProvided || "",
        employerId: jobDetails.employerId ?? 107,
        jobCompanyId: jobDetails.jobCompanyId ?? "",
        jobLocationId: jobDetails.jobLocationId ?? "",
        jobTypeCategoryId: jobDetails.jobTypeCategoryId ?? "",
        jobStatus: jobDetails.jobStatus || "Open",
      });

      if (
        resolvedLocationName === "Other" ||
        jobDetails.location === "Other" ||
        jobDetails.otherLocation
      ) {
        setShowOtherLocation(true);
        setOtherLocation(jobDetails.otherLocation || "");
      }
    }
  }, [jobDetails, jobLocationsList]);

  // Fetch Job Industries
  useEffect(() => {
    getJobIndustries()
      .then((res) => {
        const parsedTypes = Array.isArray(res)
          ? res
          : res?.data?.data || res?.data || [];
        setJobTypes(parsedTypes);
      })
      .catch((err) => {
        console.error("Error direct-fetching job industries:", err);
      });
  }, []);

  // Fetch dynamic settings
  useEffect(() => {
    const fetchFrontendDropdownSettings = async () => {
      try {
        const response = await getFrontendSettings();
        const rawData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        const expSetting = rawData.find(
          (item) => item.key === "total_work_experiences"
        );
        if (expSetting && expSetting.value) {
          const parsedExp = expSetting.value
            .split(/\r?\n/)
            .map((opt) => opt.trim())
            .filter((opt) => opt.length > 0);
          setExperienceOptions(parsedExp);
        }

        const salarySetting = rawData.find(
          (item) => item.key === "salary_range"
        );
        if (salarySetting && salarySetting.value) {
          const parsedSalary = salarySetting.value
            .split(/\r?\n/)
            .map((opt) => opt.trim())
            .filter((opt) => opt.length > 0);
          setSalaryOptions(parsedSalary);
        }
      } catch (error) {
        console.error(
          "Error setting dynamic dropdown configuration options:",
          error
        );
      }
    };

    fetchFrontendDropdownSettings();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 100, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (companyId) {
      setFormData((prev) => ({ ...prev, jobCompanyId: companyId }));
    }
  }, [companyId]);

  const onValueChange = (e, name) => {
    const value = e?.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 🎯 Vacancies: allows clearing the box, digits only, no leading zeros.
  // Clears its own error as soon as the user edits the field.
  const onVacanciesChange = (e) => {
    const inputValue = e.target.value;

    setErrors((prev) => ({ ...prev, totalNumberOfVacancies: false }));

    if (inputValue === "") {
      setFormData((prev) => ({ ...prev, totalNumberOfVacancies: "" }));
      return;
    }

    if (!/^\d+$/.test(inputValue)) return;

    const cleaned = String(parseInt(inputValue, 10));
    setFormData((prev) => ({ ...prev, totalNumberOfVacancies: cleaned }));
  };

  const onLocationChange = (selectedName) => {
    const matched = jobLocationsList.find(
      (loc) => String(loc.state) === String(selectedName)
    );

    setFormData((prev) => ({
      ...prev,
      location: selectedName,
      jobLocationId: matched
        ? matched.id
        : selectedName === "Other"
        ? "Other"
        : "",
    }));

    if (selectedName === "Other") {
      setShowOtherLocation(true);
    } else {
      setShowOtherLocation(false);
      setOtherLocation("");
    }
  };

  const validateForm = () => {
    let err = {};

    if (!formData.jobTitle) err.jobTitle = true;
    if (!formData.jobCompanyId) err.jobCompanyId = true;
    // 🎯 Vacancies must be at least 1 (empty counts as 0)
    if (Number(formData.totalNumberOfVacancies) < 1)
      err.totalNumberOfVacancies = true;
    if (!formData.jobTypeCategoryId) err.jobTypeCategoryId = true;
    if (!formData.jobLocationId) err.jobLocationId = true;
    if (showOtherLocation && !otherLocation) err.jobLocationId = true;
    if (!formData.minimumExperienceRequired)
      err.minimumExperienceRequired = true;
    if (!formData.minimumSalaryPerMonth) err.minimumSalaryPerMonth = true;
    if (!formData.jobResponsibilities) err.jobResponsibilities = true;

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return null;
    }

    setErrors({});
    return {
      ...formData,
      isActive: Number(formData.isActive || 0),
      displayOrder: Number(formData.displayOrder || 0),
      totalNumberOfVacancies: Number(formData.totalNumberOfVacancies || 0),
      minimumExperienceRequired: formData.minimumExperienceRequired,
      maximumExperienceRequired: formData.maximumExperienceRequired,
      minimumSalaryPerMonth: formData.minimumSalaryPerMonth,
      maximumSalaryPerMonth: formData.maximumSalaryPerMonth,
      gander: formData.gander || "",
      employerId: Number(formData.employerId || 0),
      jobCompanyId: Number(formData.jobCompanyId || 0),
      jobLocationId:
        formData.jobLocationId === "Other"
          ? 0
          : Number(formData.jobLocationId || 0),
      jobTypeCategoryId: Number(formData.jobTypeCategoryId || 0),
      location:
        formData.jobLocationId === "Other"
          ? otherLocation
          : formData.location || "",
      otherLocation: formData.jobLocationId === "Other" ? otherLocation : "",
    };
  };

  const handleOpenPreview = () => {
    const payload = validateForm();
    if (payload) {
      setPendingPayload(payload);
      setPreviewModal(true);
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    const payload = validateForm();
    if (payload) {
      onSubmit(payload);
    }
  };

  const handleConfirmPublishFromPreview = () => {
    setPreviewModal(false);
    if (pendingPayload) {
      onSubmit(pendingPayload);
    }
  };

  // 🎯 Keep the "create a company" modal in sync with the real companies
  // list: open it only when we know there is no company, and close it as
  // soon as one appears (the old effect could only ever open it).
  useEffect(() => {
    if (employer?.companies?.loading) return;

    if (Array.isArray(companies) && companies.length === 0) {
      setModal(true);
    } else if (Array.isArray(companies) && companies.length > 0) {
      setModal(false);
    }
  }, [companies, employer?.companies?.loading]);

  const onClose = () => navigate("/employer/job/manage");

  // Display metadata for preview UI
  const selectedCompany = Array.isArray(companies)
    ? companies.find((c) => String(c.id) === String(formData.jobCompanyId))
    : null;

  const selectedIndustry = jobTypes.find(
    (t) => String(t.id || t.jobTypeCategoryId) === String(formData.jobTypeCategoryId)
  );

  return (
    <>
      {/* Create Company Modal */}
      <Modal isOpen={modal} centered>
        <ModalBody className="p-5 text-center">
          <div className="position-absolute end-0 top-0 p-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-close"
            ></button>
          </div>
          <p>Please create a company first to continue posting jobs.</p>
          <Link to="/employer/company/add" className="btn btn-primary mt-2">
            Create a company
          </Link>
        </ModalBody>
      </Modal>

      {/* Modern Portal-Style Job Preview Modal */}
      <Modal
        isOpen={previewModal}
        toggle={() => setPreviewModal(false)}
        centered
        size="xl"
      >
        <ModalHeader toggle={() => setPreviewModal(false)}>
          <i className="uil uil-eye me-2 text-primary"></i>
          Job Post Preview - Portal Candidate View
        </ModalHeader>
        <ModalBody className="bg-light p-4">
          <div className="card shadow-sm border-0 rounded-3 p-4">
            {/* Header Banner Section */}
            <div className="d-flex align-items-start justify-content-between border-bottom pb-3 mb-4">
              <div>
                <h3 className="text-primary fw-bold mb-1">
                  {formData.jobTitle || "Job Designation Title"}
                </h3>
                <h5 className="text-muted mb-2">
                  <i className="uil uil-building me-1"></i>
                  {selectedCompany?.name || "Company Name"}
                </h5>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <Badge color="soft-primary" className="p-2 fs-6 text-dark border">
                    <i className="uil uil-map-pin-alt me-1 text-primary"></i>
                    {formData.jobLocationId === "Other"
                      ? otherLocation
                      : formData.location || "Location"}
                  </Badge>
                  <Badge color="soft-success" className="p-2 fs-6 text-dark border">
                    <i className="uil uil-bill me-1 text-success"></i>
                    {formData.minimumSalaryPerMonth || "Salary Range"}
                  </Badge>
                  <Badge color="soft-info" className="p-2 fs-6 text-dark border">
                    <i className="uil uil-briefcase-alt me-1 text-info"></i>
                    {formData.minimumExperienceRequired || "Experience"}
                  </Badge>
                  <Badge color="soft-warning" className="p-2 fs-6 text-dark border">
                    <i className="uil uil-users-alt me-1 text-warning"></i>
                    {formData.totalNumberOfVacancies || 0} Vacancies
                  </Badge>
                </div>
              </div>

   
            </div>

            {/* Content Details Grid */}
            <Row>
              <Col md={8}>
                {/* Job Responsibilities */}
                <div className="mb-4">
                  <h5 className="fw-bold text-dark border-bottom pb-2">
                    Job Description & Responsibilities
                  </h5>
                  <div
                    className="job-preview-description mt-2"
                    dangerouslySetInnerHTML={{
                      __html:
                        formData.jobResponsibilities ||
                        "<em>No details provided yet.</em>",
                    }}
                  />
                </div>

                {/* Skills & Requirements */}
                {formData.skillsAndExperience && (
                  <div className="mb-4">
                    <h5 className="fw-bold text-dark border-bottom pb-2">
                      Required Skills & Experience
                    </h5>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {formData.skillsAndExperience
                        .split(",")
                        .map((skill, index) => (
                          <span
                            key={index}
                            className="badge bg-secondary p-2 me-1"
                          >
                            {skill.trim()}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </Col>

              <Col md={4}>
                <div className="bg-white border rounded p-3 shadow-xs">
                  <h6 className="fw-bold border-bottom pb-2 mb-3">
                    Job Summary
                  </h6>
                  <ul className="list-unstyled text-muted mb-0">
                    <li className="mb-2">
                      <strong>Industry: </strong>
                      <span className="text-dark">
                        {selectedIndustry?.name ||
                          selectedIndustry?.industryName ||
                          selectedIndustry?.title ||
                          "N/A"}
                      </span>
                    </li>
                    <li className="mb-2">
                      <strong>Gender Preference: </strong>
                      <span className="text-dark">
                        {formData.gander || "Any"}
                      </span>
                    </li>
                    {formData.qualificationsRequired && (
                      <li className="mb-2">
                        <strong>Qualifications: </strong>
                        <span className="text-dark">
                          {formData.qualificationsRequired}
                        </span>
                      </li>
                    )}
                    {formData.searchKeywords && (
                      <li className="mb-2">
                        <strong>Keywords: </strong>
                        <span className="text-dark">
                          {formData.searchKeywords}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>

                {formType !== "update" && (
                  <div className="alert alert-warning mt-3 small mb-0">
                    <i className="uil uil-info-circle me-1"></i>
                    <strong>Note:</strong> Once published, the <u>Job Title</u>{" "}
                    cannot be updated later.
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button color="secondary" onClick={() => setPreviewModal(false)}>
            <i className="uil uil-edit me-1"></i> Back to Edit
          </Button>
          <Button color="success" onClick={handleConfirmPublishFromPreview}>
            <i className="uil uil-check-circle me-1"></i> Confirm &{" "}
            {formType === "update" ? "Update" : "Publish"} Job
          </Button>
        </ModalFooter>
      </Modal>

      {/* Main Form */}
      <form onSubmit={onFormSubmit} className="job-post-form shadow mt-4">
        <div className="job-post-content box-shadow-md rounded-3 p-4">
          <Row>
            {/* Company Name Selection */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="comp-name" className="form-label">
                  Company Name *
                </Label>
                <Input
                  type="select"
                  value={formData.jobCompanyId}
                  className="form-select"
                  id="comp-name"
                  name="jobCompanyId"
                  invalid={errors.jobCompanyId ? true : false}
                  onChange={(e) => onValueChange(e, "jobCompanyId")}
                >
                  <option value="">--select--</option>
                  {Array.isArray(companies) ? (
                    companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading companies...</option>
                  )}
                </Input>
              </div>
            </Col>

            {/* Job Industry Selection */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="job-categories" className="form-label">
                  Job Industry *
                </Label>
                <Input
                  type="select"
                  className="form-select"
                  id="job-categories"
                  name="jobTypeCategoryId"
                  value={formData.jobTypeCategoryId}
                  invalid={errors.jobTypeCategoryId ? true : false}
                  onChange={(e) => onValueChange(e, "jobTypeCategoryId")}
                >
                  <option value="">--select--</option>
                  {jobTypes.map((type) => (
                    <option
                      key={type.id || type.jobTypeCategoryId}
                      value={type.id || type.jobTypeCategoryId}
                    >
                      {type.name || type.industryName || type.title}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>

            {/* Job Title Search Selection */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="jobtitle" className="form-label fw-semibold">
                  Job Title / Designation *{" "}
                  {formType === "update" && (
                    <span className="text-muted fs-6 fw-normal ms-1">
                      (Non-editable)
                    </span>
                  )}
                </Label>
                <div
                  className={
                    errors.jobTitle ? "is-invalid custom-dropdown-error" : ""
                  }
                  style={
                    formType === "update"
                      ? {
                          pointerEvents: "none",
                          opacity: 0.7,
                          backgroundColor: "#e9ecef",
                          borderRadius: "0.375rem",
                          cursor: "not-allowed",
                        }
                      : {}
                  }
                >
                  <SearchableDropdown
                    options={getDropdownOptions(categories, {
                      label: "name",
                      value: "name",
                    })}
                    value={formData.jobTitle}
                    onChange={(val) => onValueChange(val, "jobTitle")}
                    placeholder="Select or Enter Job Title"
                    leftIcon={<i className="uil uil-briefcase-alt me-4"></i>}
                    isCreatable={true}
                    sortOptionsAlphabetically
                    isLoading={apiLoading}
                    isDisabled={formType === "update"}
                    disabled={formType === "update"}
                    readOnly={formType === "update"}
                  />
                </div>
                {errors.jobTitle && (
                  <div className="invalid-feedback d-block mt-1">
                    Please select a title
                  </div>
                )}
              </div>
            </Col>

            {/* Job Location Selection */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="job-location" className="form-label">
                  Job Location *
                </Label>
                <LocationSearch
                  location={formData.location}
                  setLocation={onLocationChange}
                  leftIcon={<i className="uil uil-map-pin-alt me-4"></i>}
                />
                {errors.jobLocationId && (
                  <div className="invalid-feedback d-block mt-1">
                    Please select a location
                  </div>
                )}
              </div>
            </Col>

            {/* Custom Location Field */}
            {showOtherLocation && (
              <Col lg={6}>
                <div className="mb-4">
                  <Label className="form-label">Enter Other Location *</Label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Enter Location"
                    value={otherLocation}
                    invalid={
                      errors.jobLocationId && !otherLocation ? true : false
                    }
                    onChange={(e) => {
                      setOtherLocation(e.target.value);
                    }}
                  />
                </div>
              </Col>
            )}

            {/* Total Vacancies Input */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="numVacancies" className="form-label">
                  Total Number Of Vacancies *
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="numVacancies"
                  value={formData.totalNumberOfVacancies}
                  placeholder="Enter Total Number Of Vacancies"
                  name="totalNumberOfVacancies"
                  invalid={errors.totalNumberOfVacancies ? true : false}
                  onChange={onVacanciesChange}
                />
                {errors.totalNumberOfVacancies && (
                  <div className="invalid-feedback d-block mt-1">
                    Vacancies must be at least 1
                  </div>
                )}
              </div>
            </Col>

            {/* Minimum Experience Dropdown */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="minExperience" className="form-label">
                  Minimum Experience Required *
                </Label>
                <Input
                  type="select"
                  className="form-control form-select"
                  id="minExperience"
                  name="minimumExperienceRequired"
                  value={formData.minimumExperienceRequired}
                  invalid={errors.minimumExperienceRequired ? true : false}
                  onChange={(e) =>
                    onValueChange(e, "minimumExperienceRequired")
                  }
                >
                  <option value="">Select Minimum Experience Required</option>
                  {experienceOptions.map((exp, index) => (
                    <option key={index} value={exp}>
                      {exp}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>

            {/* Minimum Salary Per Month Dropdown */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="minSalary" className="form-label">
                  Minimum Salary Per Month *
                </Label>
                <Input
                  type="select"
                  className="form-control form-select"
                  id="minSalary"
                  name="minimumSalaryPerMonth"
                  value={formData.minimumSalaryPerMonth}
                  invalid={errors.minimumSalaryPerMonth ? true : false}
                  onChange={(e) => {
                    const selectedSalary = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      minimumSalaryPerMonth: selectedSalary,
                      maximumSalaryPerMonth: selectedSalary,
                    }));
                  }}
                >
                  <option value="">Select Minimum Salary Per Month</option>
                  {salaryOptions.map((salary, index) => (
                    <option key={index} value={salary}>
                      {salary}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>

            {/* Qualifications Required TextArea */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="qualifications" className="form-label">
                  Qualifications Required
                </Label>
                <Input
                  type="textarea"
                  rows={3}
                  className="form-control"
                  id="qualifications"
                  placeholder="Any Hospitality regarding degree.."
                  name="qualificationsRequired"
                  value={formData.qualificationsRequired}
                  invalid={errors.qualificationsRequired ? true : false}
                  onChange={(e) => onValueChange(e, "qualificationsRequired")}
                />
              </div>
            </Col>

            {/* Gender Selection Dropdown */}
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="job-gander" className="form-label">
                  Choose Gender *
                </Label>
                <Input
                  type="select"
                  className="form-select"
                  id="job-gander"
                  name="gander"
                  value={formData.gander || ""}
                  invalid={errors.gander ? true : false}
                  onChange={(e) => onValueChange(e, "gander")}
                >
                  <option value="">--select--</option>
                  {ganderOptionsList.map((gander) => (
                    <option key={gander} value={gander}>
                      {gander}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>

            {/* Job Responsibilities TextArea */}
            <Col lg={12}>
              <div className="mb-4">
                <Label htmlFor="job-respon" className="form-label">
                  Job Responsibilities *
                </Label>
                <CKEditor
                  editor={ClassicEditor}
                  data={formData.jobResponsibilities || ""}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData((prev) => ({
                      ...prev,
                      jobResponsibilities: data,
                    }));
                  }}
                />
                {errors.jobResponsibilities && (
                  <div className="text-danger mt-1">
                    {errors.jobResponsibilities}
                  </div>
                )}
              </div>
            </Col>

            {/* Skills TextArea */}
            <Col lg={12}>
              <div className="mb-4">
                <Label htmlFor="skill-experience" className="form-label">
                  Skills
                </Label>
                <Input
                  type="textarea"
                  rows={3}
                  className="form-control"
                  id="skill-experience"
                  placeholder="Multitasking in high-pressure situations. ..."
                  onChange={(e) => onValueChange(e, "skillsAndExperience")}
                  invalid={errors.skillsAndExperience ? true : false}
                  name="skillsAndExperience"
                  value={formData.skillsAndExperience}
                />
              </div>
            </Col>

            {/* Search Keywords Input */}
            <Col lg={12}>
              <div className="mb-4">
                <Label htmlFor="search-keywords" className="form-label">
                  Search Keywords
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="search-keywords"
                  placeholder="E.g. #Front Office, #Manager..."
                  onChange={(e) => onValueChange(e, "searchKeywords")}
                  invalid={errors.searchKeywords ? true : false}
                  name="searchKeywords"
                  value={formData.searchKeywords}
                />
              </div>
            </Col>

            {/* Form Actions Section Panel with Preview Button */}
            <Col lg={12}>
              <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn btn-primary"
                >
                  Back
                </button>

                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="btn btn-outline-info"
                >
                  <i className="uil uil-eye me-1"></i> Preview Job
                </button>

                {loading ? (
                  <button type="button" className="btn btn-success disabled">
                    {formType === "update" ? "Update Now" : "Publish Job Now"}{" "}
                    <Spinner size="sm" />
                  </button>
                ) : (
                  <button className="btn btn-success" type="submit">
                    {formType === "update" ? "Update Now" : "Publish Job Now"}{" "}
                    <i className="mdi mdi-send"></i>
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </form>
    </>
  );
};

export default ManageJobsForm;